import React, { FC, useEffect, useMemo } from 'react';
import { Layout } from 'src/common/components/Layout';
import { CourseBundleHeader } from 'src/public/components/course-bundle-page/CourseBundleHeader';
import styled, { css } from 'styled-components';
import { colors, shadows } from 'src/config/colors';
import { LayoutBox } from 'src/common/components/LayoutBox';
import { SectionTitle } from 'src/common/components/SectionTitle';
import { below, media, widthBreakpoints } from 'src/config/breakpoints';
import { MediaQuery } from 'src/common/migration/MediaQuery';
import { LayoutPanelCategories } from 'src/common/components/Layout/LayoutPanelCategories';
import { SimpleContentLoader } from 'src/common/components/SimpleContentLoader';
import { layers } from 'src/config/layers';
import { buildRedirectUrl } from 'src/lib/router.utils';
import { useCourseBundleQuery } from 'src/common/hooks/courseBundle/useCourseBundleQuery';
import { CourseBundleActions } from 'src/public/components/course-bundle-page/CourseBundleActions';
import { VendorGetServerSideProps } from 'vendor/utils/VendorGetServerSideProps';
import { HomepageRoute } from 'src/public/publicRoutes';
import { RouteComponentProps } from 'src/common/migration/RouteComponentProps';
import { useGatsbyLikeNavigate } from 'src/common/migration/useGatsbyLikeNavigate';
import { isErrorResponse } from 'src/common/models/error';
import { StatusCodes } from 'http-status-codes';
import { coerceInt } from 'src/lib/coerceInt';
import { buildUnauthenticatedUserKeyEnhancer } from 'src/common/hooks/useCurrentUserKeyEnhancer';
import { QueryKey } from 'src/config/queryKey';
import { assertCourseBundleParams } from 'src/common/ssr/courseBundle';
import { getCourseBundleById } from 'src/common/services/courseBundle';
import { courseQueryStaleTime } from 'src/config/cache';
import { useCourseBundleEventsQuery } from 'src/common/hooks/courseBundle/useCourseBundleEvents';
import { useCourseBundleElearningsQuery } from 'src/common/hooks/courseBundle/useCourseBundleElearnings';
import { useCourseBundleMaterialsQuery } from 'src/common/hooks/courseBundle/useCourseBundleMaterialsQuery';
import { CourseBundleMaterialList } from 'src/courseBundle/components/CourseBundleMaterialList';
import { CourseBundleEventList } from 'src/courseBundle/components/CourseBundleEventList';
import { CourseBundleElearningList } from 'src/courseBundle/components/CourseBundleElearningList';
import { CourseBundlePartners } from 'src/courseBundle/components/CourseBundlePartners';
import { CourseBundleSpeakers } from 'src/courseBundle/components/CourseBundleSpeakers';
import { Button } from 'src/common/components/Button';
import { CourseBundleSection } from 'src/courseBundle/components/CourseBundleSection';
import {
  injectCourseBundleElearningsPageState,
  injectCourseBundleEventsPageState,
} from 'src/courseBundle/state';
import CheckedIcon from 'src/assets/icons/CheckedIcon';
import { useUserCourseBundleEnrollmentStatusQuery } from 'src/common/hooks/courseBundle/useUserCourseBundleEnrollmentStatusQuery';
import { CourseBundleUserEnrollmentStatus } from 'src/common/models/courseBundle';
import { useResetRedirectUrl } from 'src/common/guards/useResetRedirectUrl';
import { useJoinCourseBundleAfterRedirect } from 'src/courseBundle/hooks/useJoinCourseBundleAfterRedirect';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${media.w.min.px1200(css`
    display: grid;
    grid-template-columns: minmax(300px, 1fr) 13.625rem;
  `)};
`;

const Content = styled.div`
  order: 2;
  padding: 1.25rem 0 0.9375rem;
  width: 100%;

  ${media.w.min.px768(css`
    order: 1;
    padding: 2.5rem 3.4375rem 2.5rem 1.25rem;
    width: 68%;
  `)};

  ${media.w.min.px992(css`
    padding: 1.5625rem 1.25rem 2.1875rem 0;
    width: 60%;
  `)};

  ${media.w.min.px1200(css`
    padding: 3.4375rem 2.5rem 4.0625rem 1.125rem;
    width: 100%;
  `)};
`;

const RightPanel = styled.div`
  order: 1;
  padding-bottom: 0.9375rem;
  width: 100%;
  z-index: ${layers.widget};

  ${media.w.min.px768(css`
    order: 2;
    padding-bottom: 2.5rem;
    width: 32%;
  `)};

  ${media.w.min.px992(css`
    padding-bottom: 2.1875rem;
    width: 40%;
  `)};

  ${media.w.min.px1200(css`
    padding-bottom: 4.0625rem;
    width: 100%;
  `)};
`;

const CourseEventSelectPanel = styled.div`
  background: ${colors.white};
  border-radius: 0.25rem;
  box-shadow: ${shadows.drop1};
  left: 50%;
  padding: 1.25rem 1rem;

  margin-bottom: 1.4375rem;
  margin-top: -2.25rem;
  position: static;

  ${media.w.min.px992(css`
    margin-top: -1rem;
  `)};

  ${media.w.min.px1200(css`
    margin-top: -2.375rem;
  `)};
`;

const Section = styled.div`
  margin-bottom: 2.5rem;

  p {
    margin: 0;
  }
`;

const ShowMoreButtonContainer = styled.div`
  display: grid;
  justify-content: center;
  padding: 3.5rem 0;
`;

const AlreadySigned = styled.span`
  display: flex;
  justify-content: center;
  margin: 0.5rem;
  font-size: 0.75rem;
`;

const StyledCheckedIcon = styled(CheckedIcon)`
  margin-left: 0.5rem;
  width: 1rem;
  height: 1rem;
  color: ${colors.red};
`;

export interface CourseBundlePageProps extends RouteComponentProps {
  courseBundleId?: number;
}

injectCourseBundleElearningsPageState();
injectCourseBundleEventsPageState();

export const CourseBundlePage: FC<CourseBundlePageProps> = ({ courseBundleId }) => {
  const navigate = useGatsbyLikeNavigate();

  const {
    data: courseBundle,
    isFetching: isCourseFetching,
    isLoading: isCourseLoading,
  } = useCourseBundleQuery(courseBundleId);

  const {
    data: events,
    fetchNextPage: fetchEventsNextPage,
    hasNextPage: hasEventsNextPage,
  } = useCourseBundleEventsQuery(courseBundleId);

  const {
    data: elearnings,
    fetchNextPage: fetchElearningsNextPage,
    hasNextPage: hasElearningsNextPage,
  } = useCourseBundleElearningsQuery(courseBundleId);

  const { data: materials } = useCourseBundleMaterialsQuery(courseBundleId);
  const { data: enrollmentStatus } = useUserCourseBundleEnrollmentStatusQuery(courseBundleId);
  const isUserEnrolled = useMemo(
    () => enrollmentStatus?.value === CourseBundleUserEnrollmentStatus.Enrolled,
    [enrollmentStatus],
  );

  const { isLoading: isPostRedirectJoinLoading } = useJoinCourseBundleAfterRedirect({
    courseBundle,
    enrollmentStatus: enrollmentStatus?.value,
  }); // has to be before useResetRedirectUrl
  useResetRedirectUrl(!isCourseFetching && isUserEnrolled);

  useEffect(() => {
    if (!isCourseLoading && !courseBundle) {
      navigate(HomepageRoute);
    }
  }, [isCourseLoading, courseBundle, navigate]);

  return (
    <Layout
      leftPanel={<LayoutPanelCategories />}
      seoDescription={courseBundle?.seoDescription}
      title={courseBundle?.title}
      widgetPadding={116}
      scrollToTopButton
    >
      {isCourseLoading && isPostRedirectJoinLoading && <SimpleContentLoader />}
      {/* condition below does not get triggered correctly? */}
      {courseBundle && (
        <>
          <LayoutBox mobileBackground tabletBackground>
            <CourseBundleHeader courseBundle={courseBundle} />
            {isUserEnrolled && (
              <AlreadySigned>
                Jesteś już zapisany <StyledCheckedIcon />
              </AlreadySigned>
            )}

            {!isUserEnrolled && (
              <MediaQuery maxWidth={below(widthBreakpoints.px768)}>
                <CourseBundleActions courseBundle={courseBundle} />
              </MediaQuery>
            )}

            <Container>
              <Content>
                {courseBundle.sections.map((section) => (
                  <Section key={section.id}>
                    <CourseBundleSection heading={section.heading} content={section.content} />
                  </Section>
                ))}
              </Content>

              {!isUserEnrolled && (
                <RightPanel>
                  <MediaQuery minWidth={widthBreakpoints.px768}>
                    <CourseEventSelectPanel>
                      <CourseBundleActions courseBundle={courseBundle} />
                    </CourseEventSelectPanel>
                  </MediaQuery>
                </RightPanel>
              )}
            </Container>

            {courseBundle.partners.length !== 0 && (
              <Section>
                <SectionTitle>Nasi partnerzy</SectionTitle>
                <CourseBundlePartners partners={courseBundle.partners} />
              </Section>
            )}

            {courseBundle.speakers.length !== 0 && (
              <Section>
                <SectionTitle>Prowadzący kurs</SectionTitle>
                <CourseBundleSpeakers speakers={courseBundle.speakers} />
              </Section>
            )}

            {events?.pages[0]?.result.length !== 0 && (
              <Section>
                <SectionTitle>Lista wydarzeń kursu</SectionTitle>
                <CourseBundleEventList events={events} />
                {hasEventsNextPage && (
                  <ShowMoreButtonContainer>
                    <Button kind="border" onClick={() => fetchEventsNextPage()}>
                      Pokaż więcej wydarzeń
                    </Button>
                  </ShowMoreButtonContainer>
                )}
              </Section>
            )}

            {elearnings?.pages[0]?.result.length !== 0 && (
              <Section>
                <SectionTitle>Lista e-learningów kursu</SectionTitle>
                <CourseBundleElearningList elearnings={elearnings} />
                {hasElearningsNextPage && (
                  <ShowMoreButtonContainer>
                    <Button kind="border" onClick={() => fetchElearningsNextPage()}>
                      Pokaż więcej e-learningów
                    </Button>
                  </ShowMoreButtonContainer>
                )}
              </Section>
            )}

            {materials && materials.length !== 0 && (
              <Section>
                <SectionTitle>Materiały dodatkowe</SectionTitle>
                <CourseBundleMaterialList materials={materials} isUserEnrolled={isUserEnrolled} />
              </Section>
            )}
          </LayoutBox>
        </>
      )}
    </Layout>
  );
};

export const getServerSideProps = VendorGetServerSideProps(async (ctx) => {
  try {
    const { courseBundleId: courseBundleIdParam } = assertCourseBundleParams(ctx.params);
    const courseBundleId = coerceInt(courseBundleIdParam);

    const { key: courseBundleKey } = buildUnauthenticatedUserKeyEnhancer([
      QueryKey.CourseBundle,
      courseBundleId,
    ]);
    await ctx.queryClient.fetchQuery(courseBundleKey, () => getCourseBundleById(courseBundleId), {
      staleTime: courseQueryStaleTime,
    });

    return {
      props: {},
    };
  } catch (error) {
    if (isErrorResponse(error) && error.status === StatusCodes.NOT_FOUND) {
      return {
        notFound: true,
      };
    }
  }

  return {
    redirect: {
      destination: buildRedirectUrl(HomepageRoute, ctx),
      permanent: false,
    },
  };
});

export default CourseBundlePage;
